import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const tocItems = [
  { id: 'intro', label: 'Why Back Strength Is Critical After 60' },
  { id: 'back-anatomy', label: 'The Back Muscles You Need to Train' },
  { id: 'upper-back', label: 'Upper Back Exercises' },
  { id: 'lower-back', label: 'Lower Back Exercises' },
  { id: 'core', label: 'Core Exercises That Protect the Back' },
  { id: 'exercises-avoid', label: 'Back Exercises to Avoid After 60' },
  { id: 'posture', label: 'Fixing Posture Through Training' },
  { id: 'sample-workout', label: 'A Complete Back Workout' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { slug: 'shoulder-exercises-for-men-over-60', title: 'Shoulder Exercises for Men Over 60 (Rotator Cuff Safe)', category: 'Health & Recovery' },
  { slug: 'joint-pain-exercises-for-seniors', title: 'Exercise WITH Joint Pain: Safe Movements for Men Over 60', category: 'Health & Recovery' },
  { slug: 'best-exercises-for-men-over-60', title: '15 Best Exercises for Men Over 60 (Joint-Safe & Effective)', category: 'Strength Training' },
];

const schema = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Back Strengthening Exercises for Men Over 60',
    author: { '@type': 'Person', name: 'Mason Hale', url: 'https://masonhalefit.com' },
    publisher: { '@type': 'Organization', name: 'Mason Hale Fitness', url: 'https://masonhalefit.com' },
    datePublished: '2024-03-18',
    dateModified: '2024-04-06',
    description: 'The best back strengthening exercises for men over 60. Build a strong, pain-free back with these safe, effective movements for upper back, lower back, and core.',
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://masonhalefit.com/blog/back-exercises-for-men-over-60' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What are the best back exercises for men over 60?',
        acceptedAnswer: { '@type': 'Answer', text: 'The best back exercises for men over 60 include: dumbbell rows, resistance band rows, lat pulldowns, face pulls, bird dogs, dead bugs, and hip hinges. These build upper and lower back strength while protecting the spine.' },
      },
      {
        '@type': 'Question',
        name: 'Can I do deadlifts after 60?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes, with modifications. Romanian deadlifts (hip hinge with slight knee bend) are generally safer than conventional deadlifts for men over 60. They build the same posterior chain muscles with less spinal compression. Start light and focus on perfect form.' },
      },
      {
        '@type': 'Question',
        name: 'How do I strengthen my lower back after 60?',
        acceptedAnswer: { '@type': 'Answer', text: 'The most effective lower back exercises for men over 60 are: bird dogs, dead bugs, hip bridges, Romanian deadlifts, and back extensions. These strengthen the erector spinae and multifidus muscles that support the spine without excessive compression.' },
      },
      {
        '@type': 'Question',
        name: 'Is it safe to train back with a herniated disc?',
        acceptedAnswer: { '@type': 'Answer', text: 'Many men with herniated discs can train safely with appropriate modifications. Avoid exercises that cause pain or numbness. Focus on core stability exercises and avoid heavy spinal loading. Always consult your physician or physical therapist for personalized guidance.' },
      },
    ],
  },
]);

export default function BackExercisesPage() {
  return (
    <BlogLayout
      title="Back Strengthening Exercises for Men Over 60"
      description="The best back strengthening exercises for men over 60. Build a strong, pain-free back with safe, effective movements for upper back, lower back, and core."
      keywords="back exercises for men over 60, back strengthening exercises seniors, lower back exercises over 60, upper back exercises men over 60"
      canonicalPath="/blog/back-exercises-for-men-over-60"
      datePublished="2024-03-18"
      dateModified="2024-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Back Strengthening Exercises for Men Over 60" />

      <div className="mb-6">
        <span className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase">Health &amp; Recovery</span>
      </div>

      <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">
        Back Strengthening Exercises for Men Over 60
      </h1>

      <p className="text-gray-400 text-sm mb-8">
        By <strong className="text-white">Mason Hale</strong> &nbsp;·&nbsp; March 18, 2024 &nbsp;·&nbsp; 10 min read
      </p>

      <TableOfContents items={tocItems} />

      <section id="intro" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Why Back Strength Is Critical After 60</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Back pain is the leading cause of disability in men over 60. It limits activity, disrupts sleep, and reduces quality of life more than almost any other condition. And the most common cause? Weak back muscles.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          The <a href="https://www.nia.nih.gov/health/back-pain" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">National Institute on Aging</a> confirms that strengthening the muscles of the back and core is one of the most effective strategies for preventing and managing back pain in older adults.
        </p>
        <p className="text-gray-300 leading-relaxed">
          A strong back also means better posture, reduced injury risk, and the ability to do everything from carrying groceries to playing with grandkids without pain. This guide covers the best back exercises for men over 60 — safe, effective, and designed for aging spines.
        </p>
      </section>

      <section id="back-anatomy" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">The Back Muscles You Need to Train</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { muscle: 'Latissimus dorsi', desc: 'The large "wing" muscles. Pulling strength, posture, and shoulder stability.' },
            { muscle: 'Rhomboids & mid-traps', desc: 'Between the shoulder blades. Critical for posture and preventing the forward hunch.' },
            { muscle: 'Erector spinae', desc: 'Run along the spine. Primary lower back muscles. Protect the spine during all movement.' },
            { muscle: 'Multifidus', desc: 'Deep spinal stabilizers. Often weak in men with back pain. Core stability exercises target these.' },
          ].map((item) => (
            <div key={item.muscle} className="bg-[#111111] border border-white/10 rounded-lg p-4">
              <p className="text-[#c8a96e] text-sm font-bold mb-1">{item.muscle}</p>
              <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="upper-back" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Upper Back Exercises</h2>
        <div className="space-y-3 mb-4">
          {[
            { name: 'Dumbbell Row', sets: '3 × 10-12 each arm', desc: 'Support one hand on bench, row dumbbell to hip. The single best upper back exercise for men over 60. Builds lats, rhomboids, and biceps.' },
            { name: 'Resistance Band Row', sets: '3 × 15', desc: 'Anchor band at chest height, pull toward you with elbows close to body. Excellent for men who prefer lower impact.' },
            { name: 'Lat Pulldown (machine)', sets: '3 × 10-12', desc: 'Pull bar to upper chest. Builds the lats without the shoulder stress of pull-ups. Use a wide or neutral grip.' },
            { name: 'Face Pull (band)', sets: '3 × 15-20', desc: 'Pull band toward face with elbows high. Builds rear deltoids and rhomboids. Critical for posture and shoulder health.' },
            { name: 'Seated Cable Row', sets: '3 × 12', desc: 'Pull cable to lower chest with elbows close. Builds mid-back thickness and improves posture.' },
          ].map((item) => (
            <div key={item.name} className="bg-[#111111] border border-white/10 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <strong className="text-white text-sm">{item.name}</strong>
                <span className="text-[#c8a96e] text-xs font-bold">{item.sets}</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="lower-back" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Lower Back Exercises</h2>
        <div className="space-y-3 mb-4">
          {[
            { name: 'Romanian Deadlift', sets: '3 × 10-12', desc: 'Hinge at hips with slight knee bend, lower dumbbells along legs. The safest and most effective lower back and hamstring exercise for men over 60.' },
            { name: 'Back Extension (machine or bench)', sets: '3 × 12-15', desc: 'Hinge forward and extend back to neutral. Directly strengthens the erector spinae. Use bodyweight first, add resistance gradually.' },
            { name: 'Good Morning (light)', sets: '3 × 12', desc: 'Bar or hands behind head, hinge forward with flat back. Builds lower back and hamstring strength with controlled spinal loading.' },
            { name: 'Hip Bridge', sets: '3 × 15', desc: 'Lying on back, drive hips up. Builds glutes and lower back while lying flat — zero spinal compression.' },
          ].map((item) => (
            <div key={item.name} className="bg-[#111111] border border-white/10 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <strong className="text-white text-sm">{item.name}</strong>
                <span className="text-[#c8a96e] text-xs font-bold">{item.sets}</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="core" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Core Exercises That Protect the Back</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          A strong core is the foundation of a healthy back. These exercises build the deep stabilizers that protect the spine:
        </p>
        <ul className="space-y-3 mb-4">
          {[
            { name: 'Bird Dog', desc: 'On hands and knees, extend opposite arm and leg. Builds multifidus and erector spinae without spinal compression. 3 × 10 each side.' },
            { name: 'Dead Bug', desc: 'Lying on back, extend opposite arm and leg while keeping lower back pressed to floor. Excellent deep core activation. 3 × 10 each side.' },
            { name: 'Plank', desc: 'Hold a straight-body position on forearms and toes. Builds the entire core. Start with 20-30 seconds, build to 60+. 3 sets.' },
            { name: 'Side Plank', desc: 'Lateral core stability. Builds the obliques and quadratus lumborum that support the spine laterally. 2 × 20-30 seconds each side.' },
          ].map((item) => (
            <li key={item.name} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-arrow-right-s-line" /></span>
              <div>
                <strong className="text-white">{item.name}:</strong>
                <span className="text-gray-400 text-sm ml-1">{item.desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section id="exercises-avoid" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Back Exercises to Avoid After 60</h2>
        <ul className="space-y-2 mb-4">
          {[
            { exercise: 'Heavy conventional deadlifts', reason: 'High spinal compression. Replace with Romanian deadlifts or trap bar deadlifts.' },
            { exercise: 'Sit-ups and crunches', reason: 'Repeated spinal flexion under load. Replace with planks and dead bugs.' },
            { exercise: 'Good mornings with heavy weight', reason: 'Extreme spinal loading. Keep this exercise light or avoid entirely.' },
            { exercise: 'Twisting exercises under load', reason: 'Rotational stress on the spine. Avoid weighted Russian twists.' },
          ].map((item) => (
            <li key={item.exercise} className="flex items-start gap-3">
              <span className="text-red-400 mt-1 flex-shrink-0"><i className="ri-close-circle-line" /></span>
              <div>
                <strong className="text-white">{item.exercise}:</strong>
                <span className="text-gray-400 text-sm ml-1">{item.reason}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section id="posture" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Fixing Posture Through Training</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The forward hunch that many men develop after 60 isn't inevitable — it's the result of weak upper back muscles and tight chest muscles. The fix is simple:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Do 2 pulling exercises for every 1 pushing exercise',
            'Include face pulls and band pull-aparts in every upper body session',
            'Stretch the chest and hip flexors daily',
            'Strengthen the rhomboids and mid-traps with rows',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-checkbox-circle-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-300 leading-relaxed">
          <a href="https://www.health.harvard.edu/pain/the-importance-of-good-posture" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">Harvard Health</a> confirms that poor posture contributes to back pain, breathing difficulties, and reduced quality of life. Strength training is one of the most effective ways to correct it.
        </p>
      </section>

      <section id="sample-workout" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">A Complete Back Workout</h2>
        <div className="bg-[#111111] border border-white/10 rounded-lg p-5 mb-4">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-4">Back Day (35-40 minutes)</p>
          <div className="space-y-2">
            {[
              { exercise: 'Dumbbell Row', sets: '3 × 10-12 each' },
              { exercise: 'Lat Pulldown', sets: '3 × 10-12' },
              { exercise: 'Face Pull (band)', sets: '3 × 15-20' },
              { exercise: 'Romanian Deadlift', sets: '3 × 10-12' },
              { exercise: 'Bird Dog', sets: '3 × 10 each side' },
              { exercise: 'Dead Bug', sets: '3 × 10 each side' },
              { exercise: 'Plank', sets: '3 × 30-60 sec' },
            ].map((item) => (
              <div key={item.exercise} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                <span className="text-gray-300 text-sm">{item.exercise}</span>
                <span className="text-[#c8a96e] text-sm font-bold">{item.sets}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-gray-300 leading-relaxed">
          For a complete, progressive program that integrates back training with full-body strength work, the <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a> provides the full system — every exercise chosen for joint safety and maximum results.
        </p>
      </section>

      <section id="faq" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'What are the best back exercises for men over 60?', a: 'Dumbbell rows, resistance band rows, lat pulldowns, face pulls, bird dogs, dead bugs, and Romanian deadlifts. These build upper and lower back strength while protecting the spine.' },
            { q: 'Can I do deadlifts after 60?', a: 'Yes, with modifications. Romanian deadlifts are generally safer than conventional deadlifts for men over 60. They build the same posterior chain muscles with less spinal compression.' },
            { q: 'How do I strengthen my lower back after 60?', a: 'Bird dogs, dead bugs, hip bridges, Romanian deadlifts, and back extensions are most effective. These strengthen the erector spinae and multifidus muscles without excessive compression.' },
            { q: 'Is it safe to train back with a herniated disc?', a: 'Many men with herniated discs can train safely with appropriate modifications. Avoid exercises that cause pain or numbness. Focus on core stability exercises. Always consult your physician or physical therapist.' },
          ].map((item) => (
            <div key={item.q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <p className="text-white font-bold mb-2">{item.q}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-[#c8a96e]/10 border border-[#c8a96e]/30 rounded-lg p-6 mb-10 text-center">
        <p className="text-[#c8a96e] font-black text-lg mb-2">Build a Strong, Pain-Free Back</p>
        <p className="text-gray-300 text-sm mb-4">The Over-60 Strength Blueprint includes a complete back training protocol built around spinal safety. Mason used this approach to lose 55 lbs and get off 3 medications at age 62.</p>
        <a href="https://masonhalefit.com" className="inline-block bg-[#c8a96e] text-black font-black text-sm tracking-[0.15em] uppercase px-8 py-3 rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
          Get The Blueprint — $19.99
        </a>
      </div>

      <AuthorBio />
      <RelatedArticles articles={relatedArticles} />
    </BlogLayout>
  );
}
